
import { supabase } from "@/integrations/supabase/client";
import { CalendarConnectionManager } from "./connection-manager";
import { CalendarNotificationService } from "./notification-integration";

// Track refresh timestamps to prevent frequent refreshes
let lastTokenRefreshTime = 0;
const TOKEN_REFRESH_COOLDOWN = 5000; // 5 seconds between refresh attempts
const TOKEN_REFRESH_MAX_RETRIES = 2;
let tokenRefreshRetries = 0;

/**
 * Get all calendar connections for the current user
 */
export async function getAllCalendarConnections() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error("No authenticated user found");
      return null;
    }

    const { data: connections, error } = await supabase
      .from("calendar_connections")
      .select("*")
      .eq("user_id", user.id)
      .eq("provider", "google")
      .order("created_at", { ascending: false });
    
    if (error) {
      console.error("Error fetching calendar connections:", error);
      return null;
    }
    
    if (connections && connections.length > 0) {
      console.info(`Found ${connections.length} calendar connections`);
      
      // Check health of all connections and notify if needed
      for (const connection of connections) {
        CalendarConnectionManager.checkConnectionHealth(connection.user_id)
          .catch(error => console.error('Error checking connection health:', error));
      }
    } else {
      console.info("No calendar connections found");
    }
    
    return connections;
  } catch (err) {
    console.error("Error getting calendar connections:", err);
    return null;
  }
}

/**
 * Get all calendar connections for a specific user (host)
 */
export async function getAllCalendarConnectionsForUser(userId: string) {
  try {
    if (!userId) {
      console.error("No userId provided");
      return null;
    }

    const { data: connections, error } = await supabase
      .from("calendar_connections")
      .select("*")
      .eq("user_id", userId)
      .eq("provider", "google")
      .order("created_at", { ascending: false });
    
    if (error) {
      console.error("Error fetching calendar connections for user:", error);
      return null;
    }
    
    if (connections && connections.length > 0) {
      console.info(`Found ${connections.length} calendar connections for user ${userId}`);
    } else {
      console.info(`No calendar connections found for user ${userId}`);
    }
    
    return connections;
  } catch (err) {
    console.error("Error getting calendar connections for user:", err);
    return null;
  }
}

/**
 * Get the primary calendar connection
 */
export async function getCalendarConnection() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error("No authenticated user found");
      return null;
    }

    const { data: connection, error } = await supabase
      .from("calendar_connections")
      .select("*")
      .eq("user_id", user.id)
      .eq("provider", "google")
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    
    if (error) {
      console.error("Error fetching calendar connection:", error);
      return null;
    }
    
    if (connection) {
      console.info("Calendar connection found");
    } else {
      console.info("No calendar connection found");
    }
    
    return connection;
  } catch (err) {
    console.error("Error getting calendar connection:", err);
    return null;
  }
}

/**
 * Remove a calendar connection by ID
 */
export async function removeCalendarConnection(connectionId: string) {
  try {
    console.info(`Removing calendar connection: ${connectionId}`);
    
    const { error } = await supabase
      .from("calendar_connections")
      .delete()
      .eq("id", connectionId);
      
    if (error) {
      console.error("Error removing calendar connection:", error);
      throw new Error("Failed to remove calendar connection");
    }
    
    console.info("Calendar connection removed successfully");
    return true;
  } catch (err) {
    console.error("Error removing calendar connection:", err);
    throw err;
  }
}

/**
 * Validate if the calendar connection is working properly
 */
export async function validateCalendarConnection() {
  try {
    console.info("Validating calendar connection");
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error("No authenticated user found");
      return false;
    }
    
    const { data, error } = await supabase.functions.invoke("google-calendar", {
      body: { 
        action: "validate_connection",
        userId: user.id
      },
    });
    
    if (error) {
      console.error("Error validating calendar connection:", error);
      
      // Log validation failure but don't send alerts for temporary issues
      console.log("Calendar validation failed - automatic retry will handle this");
      
      return false;
    }
    
    console.info("Calendar connection validation result:", data);
    return data?.success === true;
  } catch (err) {
    console.error("Error validating calendar connection:", err);
    return false;
  }
}

/**
 * Connect Google Calendar (initial authorization)
 */
export async function connectGoogleCalendar(isOnboarding: boolean = false, claimToken?: string | null) {
  try {
    // Check if user is authenticated
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (!session?.access_token && !claimToken) {
      throw new Error("No active session - please log in first");
    }
    
    const headers: Record<string, string> = {};
    
    // Always add explicit onboarding context header to avoid referer-based detection issues
    headers['x-onboarding-context'] = isOnboarding ? 'true' : 'false';
    
    // Add claim token header if provided (for prospect claiming)
    if (claimToken) {
      headers['x-claim-token'] = claimToken;
    }
    
    // Add timeout wrapper to detect hanging requests
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error("Edge function call timed out after 30 seconds")), 30000);
    });
    
    const invokePromise = supabase.functions.invoke("google-auth", {
      body: { action: "authorize" },
      headers
    });
    
    const { data, error } = await Promise.race([invokePromise, timeoutPromise]) as any;
    
    if (error) {
      console.error("Google Calendar authorization error:", error);
      throw new Error(error.message || "Failed to start Google Calendar authorization");
    }
    
    if (!data?.url) {
      console.error("No authorization URL in response:", data);
      throw new Error("No authorization URL returned from the server");
    }
    
    return data;
  } catch (err: any) {
    console.error("Error connecting to Google Calendar:", err);
    throw new Error(err?.message || "Failed to connect to Google Calendar");
  }
}

/**
 * Reconnect Google Calendar (re-authorization)
 */
export async function reconnectGoogleCalendar() {
  try {
    const { data, error } = await supabase.functions.invoke("google-auth", {
      body: { action: "authorize" }
    });
    
    if (error) {
      console.error("Google Calendar authorization error:", error);
      throw new Error(error.message || "Failed to start Google Calendar authorization");
    }
    
    if (!data?.url) {
      throw new Error("No authorization URL returned from the server");
    }
    
    return data;
  } catch (err: any) {
    console.error("Error reconnecting Google Calendar:", err);
    throw new Error(err?.message || "Failed to reconnect Google Calendar");
  }
}

/**
 * Check if connection exists and is healthy
 */
export async function checkCalendarConnectionHealth() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error("No authenticated user found");
      return { hasConnection: false, needsReauth: true, error: "No authenticated user" };
    }

    // Get all connections and check the primary one
    const { data: connections, error } = await supabase
      .from("calendar_connections")
      .select("*")
      .eq("user_id", user.id)
      .eq("provider", "google")
      .order("created_at", { ascending: false });
    
    if (error || !connections || connections.length === 0) {
      console.error("Error fetching calendar connections:", error);
      return { hasConnection: false, needsReauth: true, error: "No calendar connection found" };
    }

    // Use the primary connection or the first one if no primary is set
    const primaryConnection = connections.find(conn => conn.is_primary) || connections[0];

    // Use the ProactiveTokenManager to validate and fix token health
    const { ProactiveTokenManager } = await import('./proactive-token-manager');
    
    // First validate that the API is actually working and fix any inconsistent health status
    await ProactiveTokenManager.validateAndFixTokenHealth(user.id);
    
    // Then get the updated token health
    const tokenHealth = await ProactiveTokenManager.getTokenHealth(user.id);
    
    return {
      hasConnection: true,
      needsReauth: !tokenHealth.isHealthy && tokenHealth.status === 'expired',
      error: tokenHealth.isHealthy ? null : (
        tokenHealth.status === 'expired' ? 'Calendar token has expired' :
        tokenHealth.status === 'warning' && tokenHealth.failureCount >= 5 ? 'Too many refresh failures' :
        null
      )
    };
  } catch (err) {
    console.error("Error checking calendar connection health:", err);
    return { 
      hasConnection: false, 
      needsReauth: true, 
      error: err instanceof Error ? err.message : "Unknown error" 
    };
  }
}

/**
 * Attempt to refresh the access token
 */
async function attemptTokenRefresh() {
  try {
    const now = Date.now();
    
    // Prevent frequent token refresh attempts
    if (now - lastTokenRefreshTime < TOKEN_REFRESH_COOLDOWN) {
      console.info("Token refresh attempt too soon, skipping");
      return { success: false, error: "Refresh attempt too frequent" };
    }
    
    lastTokenRefreshTime = now;
    
    const { data, error } = await supabase.functions.invoke("google-calendar", {
      body: { action: "refresh_token" }
    });
    
    if (error || !data?.success) {
      console.error("Failed to refresh token:", error || data?.error);
      
      // If refresh fails with invalid_grant, the user needs to reconnect
      if (error?.message?.includes('invalid_grant') || data?.error?.includes('invalid_grant')) {
        return { success: false, error: "Authentication expired. Please reconnect your Google Calendar." };
      }
      
      return { success: false, error: error?.message || data?.error || "Token refresh failed" };
    }
    
    console.info("Token refreshed successfully");
    return { success: true };
  } catch (err) {
    console.error("Error refreshing token:", err);
    return { 
      success: false, 
      error: err instanceof Error ? err.message : "Token refresh failed" 
    };
  }
}

/**
 * Ensure we have a valid token, refresh if needed
 */
export async function ensureValidToken() {
  const healthCheck = await checkCalendarConnectionHealth();
  
  if (!healthCheck.hasConnection) {
    console.info("No calendar connection found");
    return false;
  }
  
  if (healthCheck.needsReauth) {
    console.info("Calendar connection needs re-authentication");
    return false;
  }
  
  if (healthCheck.error) {
    console.error("Calendar connection error:", healthCheck.error);
    return false;
  }
  
  return true;
}
